import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { CodemaxBrand } from '@/components/Brand'
import { Button, Card, cx, Spinner } from '@/components/ui/primitives'
import { Field, Input, Select, Textarea } from '@/components/ui/form'
import { ApiError, publicApi } from '@/lib/api'
import { arrivalFrom, EMBED_MESSAGE, rowsOf, trackingFrom } from '@/lib/publicLeadForm'
import { useAsync } from '@/lib/useAsync'

type FieldType = 'ShortText' | 'Email' | 'Phone' | 'LongText' | 'Choice' | 'Checkbox' | 'Consent'

interface PublicField {
  key: string
  type: FieldType
  label: string
  required: boolean
  options: { value: string; label: string }[] | null
  display: string | null
  width: string | null
  helpText: string | null
  placeholder: string | null
}

interface PublicForm {
  slug: string
  versionId: string
  title: string
  intro: string | null
  submitLabel: string
  fields: PublicField[]
  captchaSiteKey: string | null
}

interface SubmitResult {
  message: string
  redirectUrl: string | null
}

type Answer = string | boolean

/**
 * A contact form marketing built and published, as the public sees it.
 *
 * <p>
 * The third page nobody signs in to reach, beside the acceptance link and the verification
 * page, and routed outside the auth guard for the same reason. It carries the Codemax mark
 * and never QuoteHero's: whoever fills it in is writing to Codemax, and the tool behind it
 * is none of their business.
 * </p>
 *
 * <p>
 * The same page serves all three ways of putting a form somewhere - the hosted address,
 * an iframe, and `embed.js`, which only writes the iframe. Embedded it drops its own page
 * chrome and reports its height to the host so the frame never scrolls inside itself.
 * </p>
 */
export function PublicLeadForm() {
  const { slug = '' } = useParams()
  const form = useAsync<PublicForm>((signal) => publicApi.get<PublicForm>(`/public/forms/${slug}`, signal), [slug])

  const arrival = useMemo(
    () =>
      arrivalFrom(
        window.location.href,
        document.referrer,
        window.location.ancestorOrigins ? Array.from(window.location.ancestorOrigins) : undefined,
      ),
    [],
  )

  const rootRef = useRef<HTMLDivElement>(null)
  useReportHeight(arrival.embedded, rootRef)
  useTransparentPage(arrival.embedded)

  const body = form.loading ? (
    <div role="status" className="flex items-center justify-center gap-2.5 py-16 text-muted">
      <Spinner className="size-4" label={null} />
      <span className="text-[13px]">Loading...</span>
    </div>
  ) : form.error || !form.data ? (
    <div className="px-6 py-12 text-center">
      <h1 className="text-[17px] font-semibold">This form is not available</h1>
      <p className="mx-auto mt-2 max-w-md text-[13px] text-muted">
        It may have been taken down, or the address may be mistyped. If you were trying to reach Codemax, please
        contact us directly.
      </p>
    </div>
  ) : (
    <LeadFormBody form={form.data} slug={slug} arrival={arrival} />
  )

  if (arrival.embedded) {
    // Transparent ground and no outer padding: it sits inside somebody else's page and
    // should look like part of it.
    return (
      <div ref={rootRef} className="bg-transparent p-1">
        {body}
      </div>
    )
  }

  return (
    <div ref={rootRef} className="min-h-full bg-wash">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-5 px-4 py-8">
        <CodemaxBrand className="h-8 self-start" />
        <Card>{body}</Card>
      </div>
    </div>
  )
}

function LeadFormBody({
  form,
  slug,
  arrival,
}: {
  form: PublicForm
  slug: string
  arrival: ReturnType<typeof arrivalFrom>
}) {
  const [answers, setAnswers] = useState<Record<string, Answer>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [problem, setProblem] = useState<string | null>(null)
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState<SubmitResult | null>(null)
  const [decoy, setDecoy] = useState('')
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)

  // When the person first saw the form. A post sooner than a person can read it is a bot.
  const renderedAt = useRef(Date.now())
  const rows = useMemo(() => rowsOf(form.fields), [form.fields])

  const set = (key: string, value: Answer) => {
    setAnswers((current) => ({ ...current, [key]: value }))
    setErrors((current) => {
      if (!(key in current)) return current
      const next = { ...current }
      delete next[key]
      return next
    })
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)

    const missing = requiredMissing(form.fields, answers)
    if (Object.keys(missing).length > 0) {
      setErrors(missing)
      return
    }

    setSending(true)

    try {
      const result = await publicApi.post<SubmitResult>(`/public/forms/${slug}`, {
        versionId: form.versionId,
        answers,
        tracking: trackingFrom(arrival),
        website: decoy,
        elapsedMs: Date.now() - renderedAt.current,
        embeddedOn: arrival.embeddedOn,
        captchaToken,
      })

      if (result.redirectUrl) {
        // An iframe cannot reliably move the page it sits in once the click that started
        // this has gone stale, so the host page is asked to do it. embed.js listens.
        if (arrival.embedded) {
          window.parent.postMessage({ type: EMBED_MESSAGE.redirect, url: result.redirectUrl }, '*')
        } else {
          window.location.assign(result.redirectUrl)
          return
        }
      }

      setDone(result)
    } catch (cause) {
      if (cause instanceof ApiError && Object.keys(cause.fieldErrors).length > 0) {
        setErrors(Object.fromEntries(Object.entries(cause.fieldErrors).map(([key, messages]) => [key, messages[0] ?? ''])))
        setProblem('Some answers need another look.')
      } else if (cause instanceof ApiError) {
        setProblem(cause.message)
      } else {
        setProblem('We could not send that. Check your connection and try again.')
      }
    } finally {
      setSending(false)
    }
  }

  if (done) {
    return (
      <div className="px-6 py-12 text-center" role="status">
        <h1 className="text-[18px] font-semibold">Thank you</h1>
        <p className="mx-auto mt-2 max-w-md text-[14px] text-ink-2">{done.message}</p>
      </div>
    )
  }

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4 px-5 py-6 sm:px-7">
      <div>
        <h1 className="font-display text-[20px] font-semibold leading-snug">{form.title}</h1>
        {form.intro && <p className="mt-1.5 text-[13.5px] text-muted">{form.intro}</p>}
      </div>

      {rows.map((row) => (
        <div key={row.map((f) => f.key).join('|')} className={cx('grid gap-4', row.length === 2 && 'sm:grid-cols-2')}>
          {row.map((field) => (
            <Question
              key={field.key}
              field={field}
              value={answers[field.key]}
              error={errors[field.key]}
              onChange={(value) => set(field.key, value)}
            />
          ))}
        </div>
      ))}

      {/* The decoy. Off-screen rather than display:none, which some bots know to skip, and
          out of the tab order and the accessibility tree so no person ever meets it. */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={decoy}
          onChange={(event) => setDecoy(event.target.value)}
        />
      </div>

      {form.captchaSiteKey && <Turnstile siteKey={form.captchaSiteKey} onToken={setCaptchaToken} />}

      {problem && (
        <p role="alert" className="rounded-md border border-crit/30 bg-crit-wash px-3 py-2 text-[13px] text-crit">
          {problem}
        </p>
      )}

      <div>
        <Button type="submit" variant="primary" loading={sending} disabled={!!form.captchaSiteKey && !captchaToken}>
          {form.submitLabel}
        </Button>
      </div>
    </form>
  )
}

function Question({
  field,
  value,
  error,
  onChange,
}: {
  field: PublicField
  value: Answer | undefined
  error: string | undefined
  onChange: (value: Answer) => void
}) {
  if (field.type === 'Consent' || field.type === 'Checkbox') {
    return (
      <div className="flex flex-col gap-1">
        <label className="flex cursor-pointer items-start gap-2.5 text-[13px] leading-relaxed text-ink-2">
          <input
            type="checkbox"
            className="mt-0.5 size-4 shrink-0 accent-[var(--accent)]"
            checked={value === true}
            onChange={(event) => onChange(event.target.checked)}
            aria-invalid={!!error}
          />
          <span>
            {field.label}
            {field.required && field.type === 'Checkbox' && <span className="ml-1 text-crit">*</span>}
          </span>
        </label>
        {error && <p className="text-[12px] text-crit">{error}</p>}
      </div>
    )
  }

  const text = typeof value === 'string' ? value : ''

  return (
    <Field label={field.label} required={field.required} hint={field.helpText ?? undefined} error={error}>
      {(id) => {
        switch (field.type) {
          case 'LongText':
            return (
              <Textarea
                id={id}
                value={text}
                placeholder={field.placeholder ?? undefined}
                maxLength={2000}
                onChange={(event) => onChange(event.target.value)}
              />
            )

          case 'Choice':
            if (field.display === 'buttons') {
              return (
                <div id={id} role="radiogroup" className="flex flex-wrap gap-2">
                  {(field.options ?? []).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="radio"
                      aria-checked={text === option.value}
                      onClick={() => onChange(option.value)}
                      className={cx(
                        'rounded-md border px-3 py-1.5 text-[13px]',
                        text === option.value
                          ? 'border-accent bg-accent-wash text-accent'
                          : 'border-line bg-surface text-ink-2 hover:border-accent',
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )
            }

            return (
              <Select id={id} value={text} onChange={(event) => onChange(event.target.value)}>
                <option value="">Choose...</option>
                {(field.options ?? []).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            )

          default:
            return (
              <Input
                id={id}
                type={field.type === 'Email' ? 'email' : field.type === 'Phone' ? 'tel' : 'text'}
                autoComplete={autoComplete(field)}
                inputMode={field.type === 'Phone' ? 'tel' : undefined}
                value={text}
                placeholder={field.placeholder ?? undefined}
                maxLength={200}
                onChange={(event) => onChange(event.target.value)}
                aria-invalid={!!error}
              />
            )
        }
      }}
    </Field>
  )
}

/** The browser's own help filling in name and contact details, where it can tell. */
function autoComplete(field: PublicField): string | undefined {
  if (field.type === 'Email') return 'email'
  if (field.type === 'Phone') return 'tel'
  if (/name/i.test(field.key)) return 'name'
  if (/company|organi[sz]ation/i.test(field.key)) return 'organization'
  return undefined
}

/**
 * Caught here so the person is told at once, without a round trip. The server checks
 * again and is the one that counts.
 */
function requiredMissing(fields: PublicField[], answers: Record<string, Answer>): Record<string, string> {
  const missing: Record<string, string> = {}

  for (const field of fields) {
    if (!field.required) continue
    const value = answers[field.key]

    if (field.type === 'Consent' || field.type === 'Checkbox') {
      if (value !== true) missing[field.key] = field.type === 'Consent' ? 'Please agree before sending.' : 'Please tick this box.'
    } else if (typeof value !== 'string' || value.trim() === '') {
      missing[field.key] = 'This is required.'
    }
  }

  return missing
}

/**
 * Tells the host page how tall the form is, so the iframe grows and shrinks with it - an
 * error appearing, a thank-you replacing the form - instead of scrolling inside itself.
 */
function useReportHeight(embedded: boolean, ref: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    if (!embedded || !ref.current || typeof ResizeObserver === 'undefined') return

    const element = ref.current
    const report = () =>
      window.parent.postMessage({ type: EMBED_MESSAGE.height, height: Math.ceil(element.getBoundingClientRect().height) }, '*')

    const observer = new ResizeObserver(report)
    observer.observe(element)
    report()

    return () => observer.disconnect()
  }, [embedded, ref])
}

/**
 * The application paints its own ground on the page itself. Inside somebody else's page that
 * ground is a grey box around the form, so an embedded form clears it and lets the host's
 * page show through.
 *
 * And it is pinned to the light theme. The visitor's dark-mode setting says nothing about
 * the page the form sits in - a marketing site is almost always light - and following it
 * would put pale text on that site's white page.
 */
function useTransparentPage(embedded: boolean) {
  useEffect(() => {
    if (!embedded) return

    const root = document.documentElement
    const targets = [root, document.body, document.getElementById('root')]
    const previous = targets.map((element) => element?.style.background ?? '')
    const previousTheme = root.getAttribute('data-theme')

    for (const element of targets) {
      if (element) element.style.background = 'transparent'
    }

    root.setAttribute('data-theme', 'light')

    return () => {
      targets.forEach((element, index) => {
        if (element) element.style.background = previous[index] ?? ''
      })

      if (previousTheme === null) root.removeAttribute('data-theme')
      else root.setAttribute('data-theme', previousTheme)
    }
  }, [embedded])
}

declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: { sitekey: string; callback: (token: string) => void; 'expired-callback': () => void }) => string
      remove: (widget: string) => void
    }
  }
}

/**
 * Cloudflare's check, loaded only for a form that has switched it on. Most forms never
 * pay for it.
 */
function Turnstile({ siteKey, onToken }: { siteKey: string; onToken: (token: string | null) => void }) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let widget: string | undefined
    let cancelled = false

    const render = () => {
      if (cancelled || !ref.current || !window.turnstile) return
      widget = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(null),
      })
    }

    if (window.turnstile) {
      render()
    } else {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.onload = render
      document.head.appendChild(script)
    }

    return () => {
      cancelled = true
      if (widget && window.turnstile) window.turnstile.remove(widget)
    }
  }, [siteKey, onToken])

  return <div ref={ref} />
}
